import { Controller } from "@hotwired/stimulus";

// Debounced GET submit for the projects search box. Turbo replaces the
// page (turbo_action: replace), so on connect we restore focus + caret
// position if this field was mid-edit before the visit, and we show a
// spinner in place of the search icon while the request is in flight.
export default class extends Controller {
  static targets = ["field", "icon", "spinner", "clear"];

  connect() {
    this.lastSubmitted = this.fieldTarget.value.trim();

    const pos = sessionStorage.getItem("projects-search-caret");
    if (pos !== null) {
      this.fieldTarget.focus();
      this.fieldTarget.setSelectionRange(Number(pos), Number(pos));
      sessionStorage.removeItem("projects-search-caret");
    }

    // turbo:submit-start/-end are dispatched on the form element per Turbo,
    // but fall back to document.documentElement if the form happens to be
    // disconnected at dispatch time — listening on `this.element` alone
    // misses that fallback and leaves the spinner stuck on. Listen at the
    // document level instead and filter to submissions from this form.
    this.onSubmitStart = (e) => { if (e.target === this.element) this.#setLoading(true); };
    this.onSubmitEnd = (e) => { if (e.target === this.element) this.#setLoading(false); };
    document.addEventListener("turbo:submit-start", this.onSubmitStart);
    document.addEventListener("turbo:submit-end", this.onSubmitEnd);

    // Safety net: never leave the spinner stuck on if something upstream
    // (network failure, a cancelled/lost event) never reports back.
    this.onBeforeCache = () => this.#setLoading(false);
    document.addEventListener("turbo:before-cache", this.onBeforeCache);
  }

  disconnect() {
    clearTimeout(this.timeout);
    clearTimeout(this.loadingSafetyTimeout);
    document.removeEventListener("turbo:submit-start", this.onSubmitStart);
    document.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    document.removeEventListener("turbo:before-cache", this.onBeforeCache);
  }

  submit() {
    this.hasClearTarget && (this.clearTarget.hidden = this.fieldTarget.value.length === 0);

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      const value = this.fieldTarget.value.trim();
      // Skip the round-trip entirely if the effective query hasn't
      // actually changed (e.g. typing then deleting back to the same text).
      if (value === this.lastSubmitted) return;

      this.lastSubmitted = value;
      sessionStorage.setItem("projects-search-caret", this.fieldTarget.selectionStart);
      this.element.requestSubmit();
    }, 300);
  }

  clear() {
    this.fieldTarget.value = "";
    this.clearTarget.hidden = true;
    this.lastSubmitted = "";
    this.element.requestSubmit();
  }

  #setLoading(isLoading) {
    if (this.hasIconTarget) this.iconTarget.hidden = isLoading;
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = !isLoading;

    clearTimeout(this.loadingSafetyTimeout);
    if (isLoading) {
      this.loadingSafetyTimeout = setTimeout(() => this.#setLoading(false), 8000);
    }
  }
}
